/**
 * statusPrimaryAction.js — single source of truth for the unified primary
 * "Follow Up" action shown on tasks-v2 checklist surfaces (sidebar quickReply
 * row AND the leads-table email/WhatsApp cells).
 *
 * WHY THIS EXISTS
 * ---------------
 * Before this module the sidebar (ChecklistItemRow) and the cell
 * (TaskChecklistField) used two different constant sets to decide the primary
 * button, and per-action composers each had their own colour/icon
 * (COMPOSER_META: Email #078DEE, WhatsApp #25D366, …). That let the two
 * surfaces drift and made the primary button look different per item type.
 *
 * The fix: ONE helper, imported by BOTH surfaces, exposing:
 *   - resolveLeadStatus(task)      — identical status-resolution order to
 *                                    TaskBodyView.handleActionZoneSuccess.
 *   - getStatusPrimaryLabel(status)— "Follow Up" for every active status,
 *                                    null for CLOSED (no forward action).
 *   - PRIMARY_ACTION_STYLE         — the ONE visual style for the primary
 *                                    button, everywhere (icon + colour). The
 *                                    per-action colours/icons stay ONLY on the
 *                                    secondary composer entries in the dropdown.
 *
 * The label/icon/colour mirror getNextActions()' FOLLOW_UP entry in
 * TaskChecklistField (the table's source of truth) so all three stay aligned.
 */

// Active forward-action statuses. CLOSED is intentionally absent — a closed
// lead has no "next" forward action, so no primary button is shown.
const ACTIVE_PRIMARY_STATUSES = new Set([
    'NEW',
    'CONSULTATION_BOOKED',
    'CONSULTATION_ATTENDED',
    // FTA arrives as either the long FSM form or the short Template form.
    'FAILED_TO_ATTEND_CONSULTATION',
    'FAILED_TO_ATTEND',
    'TREATMENT_STARTED',
]);

/**
 * The ONE primary-button style used everywhere. Matches getNextActions()'
 * FOLLOW_UP entry (icon `solar:phone-calling-bold-duotone`, colour `#0078FF`).
 * Applied uniformly regardless of the underlying checklist item type so the
 * primary button is visually identical across every item and every status.
 */
export const PRIMARY_ACTION_STYLE = Object.freeze({
    label: 'Follow Up',
    icon: 'solar:phone-calling-bold-duotone',
    color: '#0078FF',
});

/**
 * The ONE MUI `sx` for the primary "Follow Up" button — defined once, imported
 * by every surface so the primary is pixel-identical regardless of action type,
 * label length, or sidebar-vs-cell context. Fixed width so stacked primaries
 * line up. The split-button trigger style sits beside it (SPLIT_TRIGGER_SX).
 */
export const PRIMARY_BUTTON_SX = Object.freeze({
    height: 32,
    width: 150,
    minWidth: 150,
    maxWidth: 150,
    fontSize: '0.78rem',
    fontWeight: 700,
    textTransform: 'none',
    justifyContent: 'flex-start',
    bgcolor: PRIMARY_ACTION_STYLE.color,
    '&:hover': { bgcolor: PRIMARY_ACTION_STYLE.color, filter: 'brightness(0.9)' },
    px: 1.5,
    borderRadius: '8px 0 0 8px',
    boxShadow: 'none',
});

/** The split-button dropdown trigger that sits to the right of the primary. */
export const SPLIT_TRIGGER_SX = Object.freeze({
    minWidth: 32,
    width: 32,
    height: 32,
    px: 0,
    bgcolor: PRIMARY_ACTION_STYLE.color,
    '&:hover': { bgcolor: PRIMARY_ACTION_STYLE.color, filter: 'brightness(0.85)' },
    borderRadius: '0 8px 8px 0',
    borderLeft: '1px solid rgba(255,255,255,0.3)',
    boxShadow: 'none',
});

/**
 * Resolve the lead's effective status for primary-action routing.
 *
 * EXACT same order as TaskBodyView.handleActionZoneSuccess (~597-616):
 *   1. linked lead_treatment FK on the task (authoritative when hydrated)
 *   2. latest treatment from lead.lead_treatments[] by updated_at
 *   3. lead.status (lead-level; lead with no treatments)
 *   4. 'NEW' (last-resort default)
 *
 * Accepts a flexible shape so both call sites can pass what they have:
 *   - sidebar passes the lead object (has .status + .lead_treatments)
 *   - a task object (has .lead_treatment + .lead{...}) also works
 *
 * @param {object} source — task OR lead object
 * @returns {string} resolved status (never empty; falls back to 'NEW')
 */
export function resolveLeadStatus(source) {
    if (!source || typeof source !== 'object') return 'NEW';

    // The lead-bearing object: a task has `.lead`; a lead is itself.
    const lead = source.lead && typeof source.lead === 'object' ? source.lead : source;

    // 1. Direct linked lead_treatment FK (task-level when present).
    const linkedTreatment = source.lead_treatment;
    if (linkedTreatment && linkedTreatment.status) return linkedTreatment.status;

    // 2. Latest treatment by updated_at from the embedded array.
    const treatments = lead?.lead_treatments;
    if (Array.isArray(treatments) && treatments.length > 0) {
        const latest = [...treatments].sort(
            (a, b) => new Date(b?.updated_at || 0) - new Date(a?.updated_at || 0)
        )[0];
        if (latest?.status) return latest.status;
    }

    // 3. Lead-level status. 4. Default NEW.
    return lead?.status || 'NEW';
}

/**
 * The primary-button LABEL for a given status.
 * "Follow Up" for every active status; null for CLOSED / unknown.
 * Handles both FTA forms.
 *
 * @param {string} status
 * @returns {string|null}
 */
export function getStatusPrimaryLabel(status) {
    if (status && ACTIVE_PRIMARY_STATUSES.has(status)) return PRIMARY_ACTION_STYLE.label;
    return null;
}

/**
 * Convenience: does this status get a primary "Follow Up" button at all?
 * (false for CLOSED / unknown.)
 */
export function hasStatusPrimaryAction(status) {
    return getStatusPrimaryLabel(status) != null;
}
